import NoSleep from "nosleep.js";

export type ReleaseScreenWakeLock = () => void;

type ScreenWakeLockSentinel = {
  release: () => Promise<void>;
  addEventListener: (type: "release", listener: () => void) => void;
};

type ScreenWakeLockNavigator = Navigator & {
  wakeLock?: {
    request: (type: "screen") => Promise<ScreenWakeLockSentinel>;
  };
};

const isBrowser = (): boolean =>
  typeof document !== "undefined" && typeof navigator !== "undefined";

const acquireNoSleepFallback = async (): Promise<ReleaseScreenWakeLock> => {
  const noSleep = new NoSleep();
  let released = false;

  const enableNoSleep = async (): Promise<void> => {
    if (released) {
      return;
    }

    try {
      await noSleep.enable();
    } catch (_error) {
      // Autoplay policy or browser constraints — call continues without keep-awake
    }
  };

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      enableNoSleep();
    }
  };

  await enableNoSleep();
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    released = true;
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    noSleep.disable();
  };
};

const acquireScreenWakeLock = async (): Promise<ReleaseScreenWakeLock> => {
  if (!isBrowser()) {
    return () => {};
  }

  const navigatorWithWakeLock = navigator as ScreenWakeLockNavigator;

  if (!navigatorWithWakeLock.wakeLock) {
    return acquireNoSleepFallback();
  }

  let sentinel: ScreenWakeLockSentinel | null = null;
  let released = false;
  let requesting = false;

  const request = async (): Promise<void> => {
    if (
      released ||
      document.visibilityState !== "visible" ||
      sentinel ||
      requesting
    ) {
      return;
    }

    requesting = true;

    try {
      const requestedSentinel = await navigatorWithWakeLock.wakeLock.request(
        "screen",
      );
      if (released) {
        requestedSentinel.release().catch(() => {});
        return;
      }

      sentinel = requestedSentinel;
      requestedSentinel.addEventListener("release", () => {
        if (sentinel === requestedSentinel) {
          sentinel = null;
        }
      });
    } catch (_error) {
      // Permission denied or system override — call continues without keep-awake
    } finally {
      requesting = false;
    }
  };

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      request();
    }
  };

  await request();
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    released = true;
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    if (sentinel) {
      const activeSentinel = sentinel;
      sentinel = null;
      activeSentinel.release().catch(() => {});
    }
  };
};

export { acquireScreenWakeLock };
